import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LifeBuoy,
  Check,
  Search,
  ShieldAlert,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { supportTicketsApi, SupportTicketItem } from '@/api/http-client';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { cn } from '@/utils/cn';

export function AdminSupportTicketsPage() {
  const queryClient = useQueryClient();
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketItem | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'OPEN' | 'IN_PROGRESS' | 'RESOLVED'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: response, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'support-tickets', statusFilter],
    queryFn: () => supportTicketsApi.adminListTickets({ status: statusFilter === 'ALL' ? undefined : statusFilter }),
  });

  const tickets = response?.data || [];

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      supportTicketsApi.adminUpdateTicket(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-tickets'] });
      if (selectedTicket) {
        setSelectedTicket(null);
      }
    },
  });

  const resolveMutation = useMutation({
    mutationFn: supportTicketsApi.adminResolveTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-tickets'] });
      if (selectedTicket) {
        setSelectedTicket(null);
      }
    },
  });

  const filteredTickets = tickets.filter((t) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      t.subject.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.id.toLowerCase().includes(q)
    );
  });

  const openCount = tickets.filter((t) => t.status === 'OPEN').length;
  const inProgressCount = tickets.filter((t) => t.status === 'IN_PROGRESS').length;
  const resolvedCount = tickets.filter((t) => t.status === 'RESOLVED').length;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-5">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20 shadow-xs">
            <LifeBuoy className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-foreground tracking-tight">Central de Atendimento &amp; Suporte</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Gestão de chamados abertos pela Dra. Medi, solicitações de estorno/cancelamento e atendimentos de pacientes.
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={() => refetch()}
          className="rounded-xl font-bold text-xs h-10 px-4 gap-1.5 self-start sm:self-auto cursor-pointer"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Atualizar Fila</span>
        </Button>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl border border-border bg-card shadow-2xs space-y-1">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total de Chamados</span>
          <p className="text-2xl font-black text-foreground">{tickets.length}</p>
        </div>
        <div className="p-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 shadow-2xs space-y-1">
          <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Aguardando Atendimento</span>
          <p className="text-2xl font-black text-amber-600 dark:text-amber-400">{openCount}</p>
        </div>
        <div className="p-4 rounded-2xl border border-blue-500/20 bg-blue-500/5 shadow-2xs space-y-1">
          <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider">Em Análise Humana</span>
          <p className="text-2xl font-black text-blue-600 dark:text-blue-400">{inProgressCount}</p>
        </div>
        <div className="p-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 shadow-2xs space-y-1">
          <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Resolvidos / Concluídos</span>
          <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{resolvedCount}</p>
        </div>
      </div>

      {/* Barra de Filtros & Busca */}
      <div className="p-3 sm:p-4 rounded-2xl bg-card border border-border shadow-xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por protocolo, assunto ou mensagem..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-9 pr-3 text-xs sm:text-sm rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        <div className="flex items-center gap-1.5 p-1 rounded-xl bg-muted/60 border border-border text-xs font-bold">
          <button
            type="button"
            onClick={() => setStatusFilter('ALL')}
            className={cn('px-3 py-1.5 rounded-lg transition-all cursor-pointer', statusFilter === 'ALL' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground')}
          >
            Todos
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('OPEN')}
            className={cn('px-3 py-1.5 rounded-lg transition-all cursor-pointer', statusFilter === 'OPEN' ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 shadow-xs' : 'text-muted-foreground')}
          >
            Abertos
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('IN_PROGRESS')}
            className={cn('px-3 py-1.5 rounded-lg transition-all cursor-pointer', statusFilter === 'IN_PROGRESS' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300 shadow-xs' : 'text-muted-foreground')}
          >
            Em Análise
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('RESOLVED')}
            className={cn('px-3 py-1.5 rounded-lg transition-all cursor-pointer', statusFilter === 'RESOLVED' ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 shadow-xs' : 'text-muted-foreground')}
          >
            Resolvidos
          </button>
        </div>
      </div>

      {/* Lista de Chamados */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <span>Carregando chamados de suporte...</span>
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="p-12 text-center rounded-3xl border border-dashed border-border bg-card/50 text-muted-foreground text-sm">
          Nenhum chamado de suporte encontrado com os filtros atuais.
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((t) => (
            <div
              key={t.id}
              onClick={() => setSelectedTicket(t)}
              className="p-5 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            >
              <div className="space-y-1.5 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-md bg-muted text-foreground">
                    #{t.id.slice(0, 8)}
                  </span>
                  <span className={cn(
                    'px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase',
                    t.status === 'OPEN' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30' :
                    t.status === 'IN_PROGRESS' ? 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/30' :
                    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
                  )}>
                    {t.status === 'OPEN' ? 'Aguardando' : t.status === 'IN_PROGRESS' ? 'Em Análise' : 'Resolvido'}
                  </span>
                  {t.category === 'CANCELLATION' && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-rose-600" />
                      <span>Reembolso Requer Aprovação</span>
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground font-mono">
                    Prioridade: <strong>{t.priority}</strong>
                  </span>
                </div>

                <h4 className="font-bold text-foreground text-sm truncate">{t.subject}</h4>
                <p className="text-xs text-muted-foreground line-clamp-2">{t.description}</p>
              </div>

              <div className="flex items-center gap-3 shrink-0 text-xs">
                <span className="text-muted-foreground">
                  {new Date(t.createdAt).toLocaleDateString('pt-BR')} às{' '}
                  {new Date(t.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <Button size="sm" variant="outline" className="rounded-xl font-bold text-xs h-9">
                  Gerenciar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Gestão do Chamado pelo Administrador */}
      {selectedTicket && (
        <Dialog open={Boolean(selectedTicket)} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="max-w-xl w-full rounded-3xl p-6 bg-card border border-border space-y-5">
            <DialogHeader className="pb-3 border-b border-border">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-md bg-muted text-foreground">
                  Chamado #{selectedTicket.id.slice(0, 8)}
                </span>
                <span className="text-xs font-bold text-muted-foreground uppercase">
                  Categoria: {selectedTicket.category || 'Geral'}
                </span>
              </div>
              <DialogTitle className="text-lg font-bold text-foreground pt-1">{selectedTicket.subject}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 text-xs leading-relaxed">
              <div className="p-4 rounded-2xl bg-muted/30 border border-border text-foreground space-y-1">
                <span className="text-[10px] font-bold uppercase text-muted-foreground block">Mensagem / Solicitação do Paciente:</span>
                <p className="whitespace-pre-wrap text-sm font-medium">{selectedTicket.description}</p>
              </div>

              {selectedTicket.category === 'CANCELLATION' && (
                <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-900 dark:text-amber-200 text-xs flex items-start gap-2.5">
                  <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <span>
                    <strong>Regra de Conformidade:</strong> Cancelamento solicitado pelo paciente via Dra. Medi. Avalie se a consulta foi realizada ou está dentro do prazo antes de aprovar o estorno no PagBank.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-muted-foreground">
                <div>
                  <span className="block text-[10px] font-bold uppercase">Paciente ID:</span>
                  <code className="text-xs font-mono text-foreground">{selectedTicket.userId}</code>
                </div>
                <div>
                  <span className="block text-[10px] font-bold uppercase">Data de Criação:</span>
                  <span className="font-semibold text-foreground">
                    {new Date(selectedTicket.createdAt).toLocaleString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>

            {/* Ações Administrativas de Gestão */}
            <div className="pt-4 border-t border-border flex items-center justify-between gap-2.5">
              <div className="flex items-center gap-2">
                {selectedTicket.status !== 'IN_PROGRESS' && selectedTicket.status !== 'RESOLVED' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => updateStatusMutation.mutate({ id: selectedTicket.id, status: 'IN_PROGRESS' })}
                    disabled={updateStatusMutation.isPending}
                    className="rounded-xl text-xs font-bold"
                  >
                    Marcar em Análise
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {selectedTicket.status !== 'RESOLVED' && (
                  <Button
                    type="button"
                    onClick={() => resolveMutation.mutate(selectedTicket.id)}
                    disabled={resolveMutation.isPending}
                    className="rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm gap-1.5"
                  >
                    <Check className="w-4 h-4" />
                    <span>Concluir e Resolver Chamado</span>
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
